/**
 * WINGMAN INDOOR ROUTE GEOJSON ADAPTER
 * IAM-M16B-R1.8 — Multi-Floor Indoor Route Visualization
 *
 * Transforms certified IndoorRouteResponse into renderable GeoJSON FeatureCollection.
 * Strictly adheres to coordinate provenance: coordinates originate 100% from the routing engine.
 * Never invents, interpolates, or creates shortcut coordinates.
 *
 * HARD SAFETY INVARIANT:
 * Never connects coordinates across different floors with a 3D line.
 * Transitions are represented strictly as:
 * Floor Segment End + Transition Marker + Next Floor Segment Start.
 */

import type { IndoorRouteResponse } from './IndoorRouteContracts';
import type {
  IndoorRouteVisualization,
  RouteVisualizationWaypoint,
  RouteVisualizationSegment,
  RouteFloorSegment,
  RouteVerticalTransition,
  VerticalTransitionType,
} from './IndoorRouteVisualizationContracts';
import { TopologyEdgeType } from './IndoorTopologyContracts';

export class IndoorRouteGeoJsonAdapter {
  /**
   * Adapts an IndoorRouteResponse and node coordinate lookup into a provider-neutral visualization object.
   * Returns null if response indicates failure, missing endpoints, or invalid geometry.
   */
  public toVisualization(
    response: IndoorRouteResponse | null | undefined,
    nodeCoordinates: Map<string, [number, number]> | Record<string, [number, number]>,
    nodeLevels?: Map<string, number> | Record<string, number>
  ): IndoorRouteVisualization | null {
    if (!response || !response.success || !response.origin || !response.destination) {
      return null;
    }

    const getCoord = (nodeId: string): [number, number] | undefined => {
      if (nodeCoordinates instanceof Map) {
        return nodeCoordinates.get(nodeId);
      }
      return (nodeCoordinates as Record<string, [number, number]>)[nodeId];
    };

    const getLevel = (nodeId: string): number => {
      if (nodeLevels) {
        if (nodeLevels instanceof Map && nodeLevels.has(nodeId)) {
          return nodeLevels.get(nodeId)!;
        } else if ((nodeLevels as Record<string, number>)[nodeId] !== undefined) {
          return (nodeLevels as Record<string, number>)[nodeId];
        }
      }

      if (nodeId === response.origin?.nodeId) return response.origin.level;
      if (nodeId === response.destination?.nodeId) return response.destination.level;

      // Look in response segments
      for (const seg of response.segments || []) {
        if (seg.from === nodeId && (seg.edgeType as any) !== TopologyEdgeType.LEVEL_TRANSITION && (seg.edgeType as any) !== 'level_transition') {
          return seg.level;
        }
        if (seg.to === nodeId && (seg.edgeType as any) !== TopologyEdgeType.LEVEL_TRANSITION && (seg.edgeType as any) !== 'level_transition') {
          return seg.level;
        }
      }

      // Check for level in segment transition
      for (let i = 0; i < (response.segments || []).length; i++) {
        const seg = response.segments[i];
        if (seg.from === nodeId) return seg.level;
        if (seg.to === nodeId) {
          const nextSeg = response.segments[i + 1];
          if (nextSeg) return nextSeg.level;
        }
      }

      // Fallback: parse ID pattern "-L{level}" or "_L{level}"
      const match = nodeId.match(/[-_]L(-?\d+)/i);
      if (match) {
        return parseInt(match[1], 10);
      }

      return response.origin.level;
    };

    // Extract path coordinates directly from engine node map
    const pathCoords: [number, number][] = [];
    const pathNodes = response.path && response.path.length > 0
      ? [...response.path]
      : [response.origin.nodeId, response.destination.nodeId];

    for (const nodeId of pathNodes) {
      let coord = getCoord(nodeId);
      if (!coord) {
        if (nodeId === response.origin.nodeId) {
          coord = response.origin.coordinates;
        } else if (nodeId === response.destination.nodeId) {
          coord = response.destination.coordinates;
        }
      }
      if (coord) {
        pathCoords.push(coord);
      }
    }

    // Build segments
    const segments: RouteVisualizationSegment[] = (response.segments || []).map((seg) => {
      const fromCoord = getCoord(seg.from) || (seg.from === response.origin?.nodeId ? response.origin.coordinates : [0, 0]);
      const toCoord = getCoord(seg.to) || (seg.to === response.destination?.nodeId ? response.destination.coordinates : [0, 0]);
      return {
        from: seg.from,
        to: seg.to,
        distanceMeters: seg.distanceMeters,
        level: seg.level,
        coordinates: [fromCoord, toCoord],
        instruction: seg.instruction,
        edgeType: seg.edgeType,
      };
    });

    // Detect transitions from segments and node level changes
    const transitions: RouteVerticalTransition[] = [];
    const transitionIndices = new Set<number>();

    for (let i = 0; i < (response.segments || []).length; i++) {
      const seg = response.segments[i];
      const fromL = getLevel(seg.from);
      const toL = getLevel(seg.to);
      const isTransitionEdge =
        (seg.edgeType as any) === TopologyEdgeType.LEVEL_TRANSITION ||
        (seg.edgeType as any) === 'level_transition' ||
        fromL !== toL;

      if (isTransitionEdge) {
        transitionIndices.add(i);

        const depCoord = getCoord(seg.from) || response.origin.coordinates;
        const arrCoord = getCoord(seg.to) || response.destination.coordinates;

        // Determine transition type
        let type: VerticalTransitionType = 'unknown';
        const text = `${seg.from} ${seg.to} ${seg.instruction ?? ''}`.toLowerCase();
        if (text.includes('elevator')) {
          type = 'elevator';
        } else if (text.includes('escalator')) {
          type = 'escalator';
        } else if (text.includes('stair')) {
          type = 'stairs';
        } else if (text.includes('ramp')) {
          type = 'ramp';
        }

        const direction = toL > fromL ? 'up' : 'down';
        const typeLabel = type !== 'unknown' ? type : 'floor transition';
        const label = type !== 'unknown'
          ? `Take ${type} ${direction} to Level ${toL}`
          : `Continue to Level ${toL} via floor transition`;

        transitions.push({
          transitionId: `transition-${transitions.length}-${seg.from}-${seg.to}`,
          type,
          fromLevel: fromL,
          toLevel: toL,
          fromNodeId: seg.from,
          toNodeId: seg.to,
          departureCoordinate: depCoord,
          arrivalCoordinate: arrCoord,
          label,
          instruction: seg.instruction ?? label,
          provenance: 'WINGMAN_INDOOR_ROUTE_ENGINE',
        });
      }
    }

    // Build floor segments (No fake vertical lines!)
    const floorSegments: RouteFloorSegment[] = [];

    if (transitions.length === 0) {
      // Single-floor route
      const level = response.origin.level;
      floorSegments.push({
        level,
        sequence: 0,
        distanceMeters: response.totalDistanceMeters,
        coordinates: [...pathCoords],
        fromNodeId: response.origin.nodeId,
        toNodeId: response.destination.nodeId,
      });
    } else {
      // Multi-floor route: partition path into contiguous per-floor coordinate sequences
      let currentChunk: [number, number][] = [];
      let currentLevel = getLevel(pathNodes[0]);
      let chunkFromNodeId = pathNodes[0];

      for (let i = 0; i < pathNodes.length; i++) {
        const nodeId = pathNodes[i];
        const nodeCoord = getCoord(nodeId) || (i === 0 ? response.origin.coordinates : response.destination.coordinates);
        const nodeL = getLevel(nodeId);

        if (nodeL !== currentLevel && currentChunk.length > 0) {
          // Finish current floor segment
          const chunkToNodeId = pathNodes[i - 1];
          floorSegments.push({
            level: currentLevel,
            sequence: floorSegments.length,
            distanceMeters: 0, // Calculated below or aggregated
            coordinates: [...currentChunk],
            fromNodeId: chunkFromNodeId,
            toNodeId: chunkToNodeId,
          });

          // Start new floor segment at current node
          currentChunk = [nodeCoord];
          currentLevel = nodeL;
          chunkFromNodeId = nodeId;
        } else {
          currentChunk.push(nodeCoord);
        }
      }

      if (currentChunk.length > 0) {
        floorSegments.push({
          level: currentLevel,
          sequence: floorSegments.length,
          distanceMeters: 0,
          coordinates: [...currentChunk],
          fromNodeId: chunkFromNodeId,
          toNodeId: pathNodes[pathNodes.length - 1],
        });
      }
    }

    // Waypoints
    const waypoints: RouteVisualizationWaypoint[] = [];
    waypoints.push({
      nodeId: response.origin.nodeId,
      label: response.origin.label,
      coordinates: response.origin.coordinates,
      level: response.origin.level,
      kind: 'start',
    });

    for (const t of transitions) {
      waypoints.push({
        nodeId: t.fromNodeId,
        label: t.label,
        coordinates: t.departureCoordinate,
        level: t.fromLevel,
        kind: 'transition',
        transitionType: t.type,
      });
    }

    waypoints.push({
      nodeId: response.destination.nodeId,
      label: response.destination.label,
      coordinates: response.destination.coordinates,
      level: response.destination.level,
      kind: 'destination',
    });

    const routeId = `route-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;

    // Build GeoJSON features
    const features: GeoJSON.Feature[] = [];

    // 1. Floor-specific route LineStrings (NEVER connected vertically across floors)
    for (const seg of floorSegments) {
      if (seg.coordinates.length >= 2) {
        features.push({
          type: 'Feature',
          id: `${routeId}-line-seg-${seg.sequence}`,
          properties: {
            kind: 'route-line',
            routeId,
            level: seg.level,
            segmentIndex: seg.sequence,
            totalDistanceMeters: response.totalDistanceMeters,
            levelsTraversed: response.levelsTraversed,
          },
          geometry: {
            type: 'LineString',
            coordinates: seg.coordinates,
          },
        });
      }
    }

    // 2. Start point feature
    features.push({
      type: 'Feature',
      id: `${routeId}-start`,
      properties: {
        kind: 'start',
        routeId,
        label: response.origin.label,
        level: response.origin.level,
      },
      geometry: {
        type: 'Point',
        coordinates: response.origin.coordinates,
      },
    });

    // 3. Destination point feature
    features.push({
      type: 'Feature',
      id: `${routeId}-destination`,
      properties: {
        kind: 'destination',
        routeId,
        label: response.destination.label,
        level: response.destination.level,
      },
      geometry: {
        type: 'Point',
        coordinates: response.destination.coordinates,
      },
    });

    // 4. Vertical transition features (Departure on fromLevel, Arrival on toLevel)
    for (const t of transitions) {
      // Departure transition marker on fromLevel
      features.push({
        type: 'Feature',
        id: `${routeId}-transition-dep-${t.transitionId}`,
        properties: {
          kind: 'transition',
          routeId,
          level: t.fromLevel,
          fromLevel: t.fromLevel,
          toLevel: t.toLevel,
          transitionType: t.type,
          direction: 'departure',
          targetLevel: t.toLevel,
          label: t.label,
          instruction: t.instruction,
        },
        geometry: {
          type: 'Point',
          coordinates: t.departureCoordinate,
        },
      });

      // Arrival transition marker on toLevel
      features.push({
        type: 'Feature',
        id: `${routeId}-transition-arr-${t.transitionId}`,
        properties: {
          kind: 'transition',
          routeId,
          level: t.toLevel,
          fromLevel: t.fromLevel,
          toLevel: t.toLevel,
          transitionType: t.type,
          direction: 'arrival',
          targetLevel: t.toLevel,
          label: `Arrive at Level ${t.toLevel}`,
          instruction: t.instruction,
        },
        geometry: {
          type: 'Point',
          coordinates: t.arrivalCoordinate,
        },
      });
    }

    const geoJson: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features,
    };

    let bounds: [[number, number], [number, number]] | undefined;
    if (pathCoords.length > 0) {
      let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
      for (const [lng, lat] of pathCoords) {
        if (lng < minLng) minLng = lng;
        if (lat < minLat) minLat = lat;
        if (lng > maxLng) maxLng = lng;
        if (lat > maxLat) maxLat = lat;
      }
      bounds = [[minLng, minLat], [maxLng, maxLat]];
    }

    return {
      routeId,
      origin: response.origin,
      destination: response.destination,
      totalDistanceMeters: response.totalDistanceMeters,
      levelTransitions: response.levelTransitions,
      levelsTraversed: response.levelsTraversed,
      floorId: response.levelsTraversed.length === 1 ? response.levelsTraversed[0] : undefined,
      segments,
      waypoints,
      floorSegments,
      transitions,
      bounds,
      geoJson,
      provenance: {
        provider: 'WINGMAN_INDOOR_ROUTE_ENGINE',
        calculatedAt: new Date().toISOString(),
        engineVersion: '1.8.0',
        computeTimeMs: response.computeTimeMs,
      },
    };
  }
}

export const indoorRouteGeoJsonAdapter = new IndoorRouteGeoJsonAdapter();
